import { ApolloCache, FetchResult, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';

import {
  apolloClient,
  evictCache,
  GetAccountingExpenseFragment,
  GetAccountingExpenseFragmentDocument,
  UpdateAccountingExpenseInput,
  UpdateAccountingExpenseMutation,
  UpdateAccountingExpenseMutationVariables,
  UPDATE_ACCOUNTING_EXPENSE,
  updateWithInputObject,
} from '@neo.tax/shared';

type UseUpdateAccountingExpense = [
  (
    accountingExpenseId: string,
    updateAccountingExpenseInput: UpdateAccountingExpenseInput,
  ) => Promise<FetchResult<UpdateAccountingExpenseMutation>>,
  MutationResult<UpdateAccountingExpenseMutation>,
];

const UPDATE_ACCOUNTING_EXPENSE_REQUEST_KEY = 'UPDATE_ACCOUNTING_EXPENSE_REQUEST_KEY';

export const useUpdateAccountingExpense = (): UseUpdateAccountingExpense => {
  const [updateAccountingExpenseMutation, updateAccountingExpenseMutationResult] = useMutation<
    UpdateAccountingExpenseMutation,
    UpdateAccountingExpenseMutationVariables
  >(UPDATE_ACCOUNTING_EXPENSE, {
    context: {
      serializationKey: UPDATE_ACCOUNTING_EXPENSE_REQUEST_KEY,
      debounceKey: UPDATE_ACCOUNTING_EXPENSE_REQUEST_KEY,
      errorToast: {
        title: 'Error updating expense',
        description: 'Something unexpected prevented your changes from saving. Please try again.',
      },
    },
  });

  const updateAccountingExpense = useCallback(
    (
      accountingExpenseId: string,
      updateAccountingExpenseInput: UpdateAccountingExpenseInput,
    ): Promise<FetchResult<UpdateAccountingExpenseMutation>> => {
      const accountingExpenseFromCache = apolloClient.readFragment<GetAccountingExpenseFragment>({
        id: `AccountingExpense:${accountingExpenseId}`,
        fragment: GetAccountingExpenseFragmentDocument,
        fragmentName: 'GetAccountingExpense',
      });
      return updateAccountingExpenseMutation({
        variables: {
          accountingExpenseId,
          updateAccountingExpenseInput,
        },
        optimisticResponse: accountingExpenseFromCache
          ? {
              __typename: 'Mutation',
              updateAccountingExpense: updateWithInputObject(accountingExpenseFromCache, updateAccountingExpenseInput),
            }
          : undefined,
        update: (cache: ApolloCache<UpdateAccountingExpenseMutation>): void => {
          //invalidate getAccountingQreSummary cache
          evictCache(cache, ['getAccountingQreSummary']);
        },
      });
    },
    [updateAccountingExpenseMutation],
  );
  return [updateAccountingExpense, updateAccountingExpenseMutationResult];
};
